/**
 * Sezione Release nel dettaglio prodotto.
 *
 * Mostra la lista release del prodotto e, se l'utente ha PORTFOLIO_MANAGEMENT,
 * un form per creare una nuova release. Per ogni release mostra:
 *  - upload SBOM (con BOM_UPLOAD)
 *  - lista componenti con i relativi BOM (Slice 5)
 *  - azioni per aggiungere componenti e attaccare BOM
 *  - colonna "Clienti" con i nomi distinti che hanno deployments su quella release (Slice 6)
 * Le release senza forniture (nessun cliente le ha ricevute) hanno colori più tenui e si possono
 * archiviare; "Archiviate" mostra quelle archiviate e permette di ripristinarle.
 * Sopra la lista, "Confronta release" apre il confronto profondo fra due release (ReleaseCompare).
 * Dopo "Crea release" il form si chiude e la pagina scorre alla card della nuova release.
 */

import { useState, useRef, useEffect, type FormEvent, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { usePermissions } from '../../auth/index.js';
import {
  useReleases,
  useCreateRelease,
  useComponents,
  useCreateComponent,
  useAttachComponentBom,
  useDetachComponentBom,
  useBomArtifacts,
  useDeployments,
  useOpenVulnSets,
  usePendingScanSets,
  useProduct,
  useArchivedReleases,
  useSetReleaseArchived,
} from './hooks.js';
import { useCustomers } from '../customers/hooks.js';
import type {
  CreateReleaseInput,
  BomArtifact,
  ReleaseComponent,
  OpenVulnSets,
  PendingScanSets,
} from './types.js';
import { SbomViewer } from './SbomViewer.js';
import { ReleaseCompare } from './ReleaseCompare.js';
import { PendingScanBadge } from './PendingScanBadge.js';
import { ticketsReleasePath } from '../triage/routes.js';
import styles from './Products.module.css';

/**
 * Link alle vulnerabilità (triage) di un BUNDLE (foglia DT). Il conteggio dei finding APERTI viene
 * dallo snapshot più recente (stessa fonte `finding_snapshots` di lista prodotti / vista globale
 * CVE — niente lettura live da DT). Il link punta a /tickets/{dtProjectUuid del bundle}, dove
 * l'analista lavora sui finding live. Mostra "-" se il bundle non è ancora su DT.
 */
function VulnLink({
  dtProjectUuid,
  testId,
  openCount,
}: {
  dtProjectUuid?: string;
  testId: string;
  openCount: number;
}) {
  if (!dtProjectUuid) {
    return (
      <span className={styles.vulnPillNone} data-testid={`vuln-link-${testId}`}>
        — nessun SBOM
      </span>
    );
  }
  return (
    <Link
      to={ticketsReleasePath(dtProjectUuid)}
      className={`${styles.vulnPill} ${openCount > 0 ? styles.vulnPillHot : styles.vulnPillZero}`}
      title="Vulnerabilità aperte nel bundle — apri il triage"
      data-testid={`vuln-link-${testId}`}
    >
      {`${openCount} vulnerabilità`}
    </Link>
  );
}

/**
 * Compone il CPE 2.3 completo di una release da prefix (Product) + suffix (Release).
 * Rispecchia lato client `resolveReleaseCpe` di product-service/src/domain/cpe.ts:
 * undefined se manca uno dei due pezzi (nessun CPE valido da mostrare).
 */
function resolveReleaseCpe(cpePrefix?: string, cpeSuffix?: string): string | undefined {
  if (!cpePrefix || !cpeSuffix) return undefined;
  return `${cpePrefix}:${cpeSuffix}`;
}

/**
 * Converte un File in stringa base64 tramite FileReader.readAsDataURL.
 *
 * Compatibile sia con i browser reali sia con jsdom (usato nei test):
 * usa l'evento onload di FileReader, che jsdom implementa correttamente
 * ora che il file viene passato via onChange (non da fileRef.current.files).
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Rimuove il prefisso "data:<mime>;base64,"
      const base64 = result.split(',')[1] ?? result;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Impossibile leggere il file'));
    reader.readAsDataURL(file);
  });
}

// ---------------------------------------------------------------------------
// Slice 5 — Aggiunta BOM inline (libreria o upload) nella card bundle
// ---------------------------------------------------------------------------

/**
 * BundleBomAdder: pannello inline (niente modal) per allegare un BOM a un bundle, aperto dalla
 * card con "+ Aggiungi BOM". UX a 2 click:
 *  - Upload diretto: "Carica file BOM" apre il selettore del SO; la selezione di un file avvia
 *    subito l'upload (formato di default SBOM; toggle SBOM/HBOM per HBOM). Nessun pulsante di
 *    conferma: 1 click per aprire il picker + 1 click per selezionare = 2 click totali.
 *  - Libreria combinata: artefatti del prodotto (scope=product) + artefatti di sistema
 *    (scope=all, deduplicati rispetto al prodotto), già allegati disabilitati. 1 click su
 *    "Aggiungi" allega l'artefatto esistente.
 * Le mutation invalidano le query bundle + libreria: il pannello resta aperto e riflette i
 * cambiamenti. La rimozione dei BOM allegati avviene nelle righe della card (non qui).
 */
function BundleBomAdder({
  productId,
  version,
  bundle,
  onClose,
}: {
  productId: string;
  version: string;
  bundle: ReleaseComponent;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  // Formato dell'upload diretto: di default SBOM (caso più comune). HBOM richiede un click
  // extra sul toggle prima di "Carica file BOM".
  const [uploadFormat, setUploadFormat] = useState<'hbom' | 'sbom'>('sbom');

  const { data: artefattiProdotto = [], isLoading: loadingProdotto } = useBomArtifacts({
    scope: 'product',
    productId,
    q: q.trim() || undefined,
  });
  const { data: artefattiSistema = [], isLoading: loadingSistema } = useBomArtifacts({
    scope: 'all',
    q: q.trim() || undefined,
  });

  const attach = useAttachComponentBom(productId, version);

  // Artefatti già allegati a questo bundle → disabilitati nella lista (niente doppioni).
  const attachedIds = new Set(bundle.boms.map((b) => b.artifact.id));
  // Il sistema include anche quelli del prodotto: li mostriamo solo nella prima sezione.
  const productIds = new Set(artefattiProdotto.map((a) => a.id));
  const artefattiSistemaUnici = artefattiSistema.filter((a) => !productIds.has(a.id));

  function handleSeleziona(bomArtifactId: number) {
    attach.mutate({ componentId: bundle.id, input: { bomArtifactId } });
  }

  // Upload diretto: la selezione del file avvia subito l'upload con filename = nome del file.
  async function handleFileSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const bomBase64 = await fileToBase64(file);
    attach.mutate({
      componentId: bundle.id,
      input: { bomBase64, format: uploadFormat, filename: file.name },
    });
    // Reset per consentire la riesecuzione dello stesso file.
    e.target.value = '';
  }

  const isLoading = loadingProdotto || loadingSistema;

  function renderRow(a: BomArtifact) {
    const gia = attachedIds.has(a.id);
    return (
      <li key={a.id} className={styles.bomRow} data-testid={`bom-pick-row-${a.id}`}>
        <span className={styles.badge}>{a.format.toUpperCase()}</span>
        <span className={styles.bomFilename} style={{ flex: 1 }}>
          {a.filename ?? '(senza nome)'}
        </span>
        <span className={styles.bomDate}>{new Date(a.uploadedAt).toLocaleDateString('it-IT')}</span>
        {gia ? (
          <button type="button" className={`${styles.btnSecondary} ${styles.btnSmall}`} disabled>
            Già allegato
          </button>
        ) : (
          <button
            type="button"
            className={`${styles.btnPrimary} ${styles.btnSmall}`}
            onClick={() => handleSeleziona(a.id)}
            disabled={attach.isPending}
            data-testid={`btn-seleziona-artefatto-${a.id}`}
          >
            Aggiungi
          </button>
        )}
      </li>
    );
  }

  return (
    <section className={styles.bomAdder} data-testid="aggiungi-bom-area">
      {/* Barra azione: upload diretto (toggle formato + "Carica file BOM") + Chiudi.
          L'upload è auto-submit: nessun pulsante "Carica e allega". */}
      <div className={styles.bomAdderBar}>
        <div className={styles.bomUploadInline}>
          <select
            id="bom-upload-format"
            className={`${styles.formSelect} ${styles.btnSmall}`}
            value={uploadFormat}
            onChange={(e) => setUploadFormat(e.target.value as 'hbom' | 'sbom')}
            disabled={attach.isPending}
            data-testid="select-upload-format"
            aria-label="Formato BOM"
          >
            <option value="sbom">SBOM</option>
            <option value="hbom">HBOM</option>
          </select>
          <label className={`${styles.btnPrimary} ${styles.btnSmall}`}>
            {attach.isPending ? 'Upload…' : 'Carica file BOM'}
            <input
              type="file"
              accept="application/json,.json,.xml,.cdx"
              onChange={(e) => {
                void handleFileSelected(e);
              }}
              disabled={attach.isPending}
              data-testid="input-upload-bom-file"
              hidden
            />
          </label>
          {attach.isError && (
            <span className={styles.inlineMutError} data-testid="errore-allega-bom">
              {attach.error instanceof Error
                ? attach.error.message
                : "Errore durante l'allegato del BOM."}
            </span>
          )}
        </div>
        <button
          type="button"
          className={`${styles.btnSecondary} ${styles.btnSmall}`}
          onClick={onClose}
          data-testid="btn-chiudi-aggiungi-bom"
          style={{ marginLeft: 'auto' }}
        >
          Chiudi
        </button>
      </div>

      {/* Libreria combinata: ricerca + sezioni prodotto / sistema (sempre visibili). */}
      <div className={styles.bomAdderFilters}>
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per filename…"
          className={styles.formInput}
          style={{ flex: 1 }}
          data-testid="input-bom-search"
        />
      </div>

      {isLoading && <p className={styles.bomLibHint}>Caricamento…</p>}
      {!isLoading && artefattiProdotto.length === 0 && artefattiSistemaUnici.length === 0 && (
        <p className={styles.bomLibHint}>Nessun artefatto trovato.</p>
      )}

      {!isLoading && artefattiProdotto.length > 0 && (
        <div className={styles.bomLibSection} data-testid="bom-lib-prodotto">
          <p className={styles.bomLibTitle}>In questo prodotto</p>
          <ul className={styles.bomList}>{artefattiProdotto.map(renderRow)}</ul>
        </div>
      )}
      {!isLoading && artefattiSistemaUnici.length > 0 && (
        <div className={styles.bomLibSection} data-testid="bom-lib-sistema">
          <p className={styles.bomLibTitle}>BOM nel sistema</p>
          <ul className={styles.bomList}>{artefattiSistemaUnici.map(renderRow)}</ul>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Slice 5 — Bundle di una release
// ---------------------------------------------------------------------------

/**
 * ReleaseCard: un gruppo Release (Proposta A — release come gruppo, bundle come card).
 *
 * Header: versione + riepilogo (n. bundle / file BOM / vulnerabilità / clienti) + azione di
 * release "Clienti". Corpo: una card per bundle (nome + pill vulnerabilità + azioni "Vedi SBOM" /
 * "+ Aggiungi BOM"); i file BOM allegati si rimuovono inline dalle loro righe e si aggiungono dal
 * pannello inline BundleBomAdder (niente modal). Footer: form aggiungi bundle.
 */
function ReleaseCard({
  productId,
  version,
  dtProjectUuid,
  cpeFull,
  purl,
  puoGestire,
  deployments,
  customerMap,
  openSets,
  pendingSets,
  fornitureCaricate,
  cardRef,
}: {
  productId: string;
  version: string;
  dtProjectUuid?: string;
  cpeFull?: string;
  purl?: string;
  puoGestire: boolean;
  deployments: { customerId: number; releaseVersion: string }[];
  customerMap: Map<number, string>;
  openSets?: OpenVulnSets;
  pendingSets?: PendingScanSets;
  /** Forniture lette: finché non lo sono, la release non si marca "senza forniture". */
  fornitureCaricate: boolean;
  /** Registra il nodo della card (scroll + focus dopo la creazione). */
  cardRef?: (el: HTMLDivElement | null) => void;
}) {
  const { data: bundle = [], isLoading } = useComponents(productId, version);
  const createComp = useCreateComponent(productId, version);
  const detach = useDetachComponentBom(productId, version);
  const [nomeNuovo, setNomeNuovo] = useState('');
  // Id del bundle con il pannello "Aggiungi BOM" aperto inline, null se nessuno.
  const [addOpenId, setAddOpenId] = useState<number | null>(null);
  // Id del bundle di cui è aperta la vista SBOM (lazy), null se nessuno.
  const [sbomOpenId, setSbomOpenId] = useState<number | null>(null);

  function handleAggiungi(e: FormEvent) {
    e.preventDefault();
    createComp.mutate(
      { name: nomeNuovo.trim() || undefined },
      { onSuccess: () => setNomeNuovo('') },
    );
  }

  // Riepilogo di release per l'header.
  const bomCount = bundle.reduce((n, c) => n + c.boms.length, 0);
  const relVuln = (dtProjectUuid && openSets?.openCountByRelease.get(dtProjectUuid)) || 0;
  const fornitureRelease = deployments.filter((d) => d.releaseVersion === version);
  const clientiCount = new Set(fornitureRelease.map((d) => d.customerId)).size;
  // Nessun cliente ha ricevuto la release: colori più tenui + azione "Archivia".
  const senzaForniture = fornitureCaricate && fornitureRelease.length === 0;
  const archivia = useSetReleaseArchived(productId);

  function handleArchivia() {
    if (
      !window.confirm(
        `Archiviare la release ${version}?\n\nNessun cliente l'ha ricevuta. La release esce dalla ` +
          'lista e dalle scansioni automatiche; si può ripristinare da «Archiviate».',
      )
    ) {
      return;
    }
    archivia.mutate({ version, archived: true });
  }

  return (
    <div
      ref={cardRef}
      tabIndex={-1}
      aria-label={`Release ${version}`}
      className={`${styles.relGroup} ${senzaForniture ? styles.relGroupSenzaForniture : ''}`}
      data-testid={`release-row-${version}`}
      data-forniture={fornitureCaricate ? (senzaForniture ? 'nessuna' : 'presenti') : undefined}
    >
      <div className={styles.relHead}>
        <div className={styles.relHeadTop}>
          <span className={styles.relVersion}>{version}</span>
          {senzaForniture && (
            <span
              className={styles.senzaFornitureTag}
              title="Nessun cliente ha ricevuto questa release"
              data-testid={`release-senza-forniture-${version}`}
            >
              Senza forniture
            </span>
          )}
          <div className={styles.relSummary}>
            <span className={styles.summaryChip}>
              {bundle.length} {bundle.length === 1 ? 'bundle' : 'bundle'}
            </span>
            <span className={styles.summaryChip}>{bomCount} file BOM</span>
            <span
              className={`${styles.summaryChip} ${relVuln > 0 ? styles.summaryChipHot : ''}`}
              data-testid={`release-vuln-summary-${version}`}
            >
              {relVuln} vulnerabilità
            </span>
            {/* Pending-scan a livello release: ≥1 bundle modificato dopo l'ultima scansione. */}
            {!!(dtProjectUuid && pendingSets?.releaseUuids.has(dtProjectUuid)) && (
              <PendingScanBadge testId={`release-pending-${version}`} />
            )}
            <span className={styles.summaryChip}>
              {clientiCount} {clientiCount === 1 ? 'cliente' : 'clienti'}
            </span>
          </div>
          <div className={styles.relActions}>
            <ClientiAzione version={version} deployments={deployments} customerMap={customerMap} />
            {puoGestire && senzaForniture && (
              <button
                type="button"
                className={`${styles.btnSecondary} ${styles.btnSmall}`}
                onClick={handleArchivia}
                disabled={archivia.isPending}
                title="Archivia la release (solo release senza forniture)"
                data-testid={`btn-archivia-release-${version}`}
              >
                {archivia.isPending ? 'Archiviazione…' : 'Archivia'}
              </button>
            )}
          </div>
        </div>
        {archivia.isError && (
          <span
            className={styles.inlineMutError}
            role="alert"
            data-testid={`errore-archivia-release-${version}`}
          >
            {archivia.error.message}
          </span>
        )}
        {/* CPE/PURL della release: valore completo nel title (tooltip) e nel testo — troncati
            solo visivamente via CSS ellipsis, quindi selezionabili/copiabili per intero. */}
        {(cpeFull || purl) && (
          <div className={styles.relIdentifiers}>
            {cpeFull && (
              <span
                className={styles.relIdentifierValue}
                title={cpeFull}
                data-testid={`release-cpe-${version}`}
              >
                CPE: {cpeFull}
              </span>
            )}
            {purl && (
              <span
                className={styles.relIdentifierValue}
                title={purl}
                data-testid={`release-purl-${version}`}
              >
                PURL: {purl}
              </span>
            )}
          </div>
        )}
      </div>

      <div className={styles.bundleCards} data-testid={`sezione-componenti-${version}`}>
        {isLoading && <span className={styles.bundleEmpty}>Caricamento…</span>}
        {!isLoading && bundle.length === 0 && (
          <span className={styles.bundleEmpty}>Nessun bundle in questa release.</span>
        )}
        {!isLoading &&
          bundle.map((comp) => (
            <div
              className={styles.bundleCard}
              key={comp.id}
              data-testid={`componente-row-${comp.id}`}
            >
              <div className={styles.bundleCardTop}>
                <span className={styles.bundleName}>{comp.name ?? `Bundle #${comp.id}`}</span>
                {/* Vulnerabilità aperte del bundle dallo snapshot più recente (foglia DT). */}
                <VulnLink
                  dtProjectUuid={comp.dtProjectUuid}
                  testId={`bundle-${comp.id}`}
                  openCount={
                    (comp.dtProjectUuid && openSets?.openCountByBundle.get(comp.dtProjectUuid)) || 0
                  }
                />
                {/* Pending-scan del bundle: modificato dopo l'ultima scansione (foglia DT). */}
                {!!(comp.dtProjectUuid && pendingSets?.bundleUuids.has(comp.dtProjectUuid)) && (
                  <PendingScanBadge testId={`bundle-pending-${comp.id}`} />
                )}
                <span className={styles.bundleActions}>
                  <button
                    type="button"
                    className={`${styles.btnSecondary} ${styles.btnSmall}`}
                    onClick={() => setSbomOpenId((cur) => (cur === comp.id ? null : comp.id))}
                    data-testid={`btn-sbom-bundle-${comp.id}`}
                  >
                    {sbomOpenId === comp.id ? 'Nascondi SBOM' : 'Vedi SBOM'}
                  </button>
                  {puoGestire && (
                    <button
                      type="button"
                      className={`${styles.btnSecondary} ${styles.btnSmall}`}
                      onClick={() => setAddOpenId((cur) => (cur === comp.id ? null : comp.id))}
                      aria-expanded={addOpenId === comp.id}
                      data-testid={`btn-aggiungi-bom-${comp.id}`}
                    >
                      {addOpenId === comp.id ? 'Chiudi' : '+ Aggiungi BOM'}
                    </button>
                  )}
                </span>
              </div>

              {/* Vista SBOM del bundle (export CycloneDX dalla foglia DT), lazy. */}
              {sbomOpenId === comp.id && (
                <div style={{ marginTop: '0.6rem' }}>
                  <SbomViewer productId={productId} version={version} componentId={comp.id} />
                </div>
              )}

              {/* BOM allegati al bundle: rimozione inline (con permesso) */}
              {comp.boms.length > 0 && (
                <div className={styles.bomList}>
                  {comp.boms.map((b) => (
                    <div key={b.id} className={styles.bomRow} data-testid={`bom-row-${b.id}`}>
                      <span className={styles.badge}>{b.artifact.format.toUpperCase()}</span>
                      <span className={styles.bomFilename}>
                        {b.artifact.filename ?? '(senza nome)'}
                      </span>
                      {puoGestire && (
                        <button
                          type="button"
                          className={styles.bomRemove}
                          onClick={() =>
                            detach.mutate({ componentId: comp.id, membershipId: b.id })
                          }
                          disabled={detach.isPending}
                          title="Rimuovi questo BOM dal bundle"
                          data-testid={`btn-stacca-bom-${b.id}`}
                        >
                          Rimuovi
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Pannello inline "Aggiungi BOM" (libreria o upload), aperto su richiesta. */}
              {puoGestire && addOpenId === comp.id && (
                <BundleBomAdder
                  productId={productId}
                  version={version}
                  bundle={comp}
                  onClose={() => setAddOpenId(null)}
                />
              )}
            </div>
          ))}
      </div>

      {/* Form aggiunta bundle (footer della release) */}
      {puoGestire && (
        <form onSubmit={handleAggiungi} className={styles.addBundleRow}>
          <input
            type="text"
            value={nomeNuovo}
            onChange={(e) => setNomeNuovo(e.target.value)}
            placeholder="Nome bundle (opzionale)"
            className={styles.addBundleInput}
            data-testid={`input-nome-componente-${version}`}
          />
          <button
            type="submit"
            className={`${styles.btnPrimary} ${styles.btnSmall}`}
            disabled={createComp.isPending}
            data-testid={`btn-aggiungi-componente-${version}`}
          >
            {createComp.isPending ? 'Aggiunta…' : '+ Bundle'}
          </button>
          {createComp.isError && (
            <span
              className={styles.inlineMutError}
              data-testid={`errore-aggiungi-componente-${version}`}
            >
              {createComp.error instanceof Error
                ? createComp.error.message
                : "Errore durante l'aggiunta del bundle."}
            </span>
          )}
        </form>
      )}
    </div>
  );
}

/**
 * Form per creare una nuova release.
 *
 * Crea solo la release: i BOM si gestiscono poi nella colonna Bundle della release
 * (modal "Modifica bundle"), non da qui.
 */
/**
 * Parti del CPE 2.3 suffix (coda Release) DOPO la `version`, nell'ordine canonico.
 * Il prefix Product è `cpe:2.3:<part>:<vendor>:<product>`; la coda completa è
 * `<version>:<update>:<edition>:<language>:<sw_edition>:<target_sw>:<target_hw>:<other>`.
 * La `version` NON è qui: coincide con la "Versione" della release (campo unico, no duplicati).
 * Queste 7 parti partono da `*` (any).
 */
const CPE_SUFFIX_FIELDS = [
  { key: 'update', label: 'Update' },
  { key: 'edition', label: 'Edition' },
  { key: 'language', label: 'Language' },
  { key: 'swEdition', label: 'SW edition' },
  { key: 'targetSw', label: 'Target SW' },
  { key: 'targetHw', label: 'Target HW' },
  { key: 'other', label: 'Other' },
] as const;

type CpeSuffixKey = (typeof CPE_SUFFIX_FIELDS)[number]['key'];
type CpeSuffixParts = Record<CpeSuffixKey, string>;

const CPE_SUFFIX_DEFAULTS: CpeSuffixParts = {
  update: '*',
  edition: '*',
  language: '*',
  swEdition: '*',
  targetSw: '*',
  targetHw: '*',
  other: '*',
};

/**
 * Compone il `cpeSuffix` (8 campi `:`-separati) da `version` (= versione release) + le 7 parti.
 * Ritorna undefined se la version è vuota → release creata senza CPE
 * (percorso valido: resolveReleaseCpe ignora il prefix se manca il suffix).
 * Ogni parte vuota ricade su `*`.
 */
function composeCpeSuffix(version: string, parts: CpeSuffixParts): string | undefined {
  const v = version.trim();
  if (!v) return undefined;
  const norm = (s: string) => s.trim() || '*';
  return [
    v,
    norm(parts.update),
    norm(parts.edition),
    norm(parts.language),
    norm(parts.swEdition),
    norm(parts.targetSw),
    norm(parts.targetHw),
    norm(parts.other),
  ].join(':');
}

function NuovaReleaseForm({
  productId,
  onCreated,
}: {
  productId: string;
  /** Release creata con successo (versione): il chiamante chiude il form e porta alla card. */
  onCreated?: (version: string) => void;
}) {
  const [version, setVersion] = useState('');
  const [cpeParts, setCpeParts] = useState<CpeSuffixParts>(CPE_SUFFIX_DEFAULTS);
  const [marketEntryDate, setMarketEntryDate] = useState('');
  const [supportEndDate, setSupportEndDate] = useState('');

  const createRelease = useCreateRelease(productId);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const cpeSuffix = composeCpeSuffix(version, cpeParts);
    const input: CreateReleaseInput = {
      version: version.trim(),
      ...(cpeSuffix ? { cpeSuffix } : {}),
      ...(marketEntryDate ? { marketEntryDate } : {}),
      ...(supportEndDate ? { supportEndDate } : {}),
    };
    try {
      const created = await createRelease.mutateAsync(input);
      setVersion('');
      setCpeParts(CPE_SUFFIX_DEFAULTS);
      setMarketEntryDate('');
      setSupportEndDate('');
      onCreated?.(created?.version ?? input.version);
    } catch {
      // Gli errori sono mostrati dallo stato della mutation (createRelease).
    }
  }

  const inCorso = createRelease.isPending;

  return (
    <div className={styles.formCard} data-testid="form-nuova-release">
      <p className={styles.formTitle}>Nuova release</p>
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
      >
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="release-version">
              Versione *
            </label>
            <input
              id="release-version"
              className={styles.formInput}
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              required
              placeholder="es. 1.0.0"
              data-testid="input-release-version"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="release-marketEntry">
              Data immissione mercato
            </label>
            <input
              id="release-marketEntry"
              type="date"
              className={styles.formInput}
              value={marketEntryDate}
              onChange={(e) => setMarketEntryDate(e.target.value)}
              data-testid="input-release-marketEntry"
            />
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel} htmlFor="release-supportEnd">
              Fine supporto
            </label>
            <input
              id="release-supportEnd"
              type="date"
              className={styles.formInput}
              value={supportEndDate}
              onChange={(e) => setSupportEndDate(e.target.value)}
              data-testid="input-release-supportEnd"
            />
          </div>
        </div>

        {/* CPE suffix a campi separati. La `version` (sopra) è la prima parte; queste 7
            completano la coda CPE 2.3. Default `*` (any). Il CPE pieno = cpePrefix ⊕ suffix. */}
        <fieldset
          data-testid="cpe-suffix-fields"
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            marginTop: '0.5rem',
          }}
        >
          <legend
            style={{
              fontSize: '0.8rem',
              color: 'var(--color-text-secondary, #666)',
              padding: '0 0.4rem',
            }}
          >
            CPE suffix (coda dopo la versione) — default <code>*</code>
          </legend>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {CPE_SUFFIX_FIELDS.map((f) => (
              <div
                key={f.key}
                className={styles.formGroup}
                style={{ minWidth: '7rem', flex: '1 1 7rem' }}
              >
                <label
                  className={styles.formLabel}
                  htmlFor={`release-cpe-${f.key}`}
                  style={{ fontSize: '0.78rem' }}
                >
                  {f.label}
                </label>
                <input
                  id={`release-cpe-${f.key}`}
                  className={styles.formInput}
                  value={cpeParts[f.key]}
                  onChange={(e) => setCpeParts((prev) => ({ ...prev, [f.key]: e.target.value }))}
                  placeholder="*"
                  data-testid={`input-release-cpe-${f.key}`}
                />
              </div>
            ))}
          </div>
          {/* Anteprima del suffix composto (vuota finché manca la versione). */}
          <p
            style={{ fontSize: '0.78rem', color: '#888', margin: '0.4rem 0 0' }}
            data-testid="cpe-suffix-preview"
          >
            {composeCpeSuffix(version, cpeParts) ?? '(nessun CPE: indicare una versione)'}
          </p>
        </fieldset>

        <div className={styles.formActions}>
          <button
            type="submit"
            className={styles.btnPrimary}
            disabled={inCorso}
            data-testid="btn-crea-release"
          >
            {createRelease.isPending ? 'Creazione…' : 'Crea release'}
          </button>
          {createRelease.isError && (
            <span className={styles.inlineMutError} data-testid="errore-crea-release">
              {createRelease.error instanceof Error
                ? createRelease.error.message
                : 'Errore durante la creazione'}
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Slice 6 — azione "Clienti" nella colonna Azioni (popup con la lista)
// ---------------------------------------------------------------------------

/**
 * ClientiAzione: voce "Clienti" della colonna Azioni. Al click apre un popup con i
 * nomi distinti dei clienti che hanno un deployment su quella releaseVersion
 * (join con useDeployments + useCustomers). Se non ci sono deployment, il popup
 * mostra un messaggio dedicato.
 */
function ClientiAzione({
  version,
  deployments,
  customerMap,
}: {
  version: string;
  deployments: { customerId: number; releaseVersion: string }[];
  customerMap: Map<number, string>;
}) {
  const [aperto, setAperto] = useState(false);

  // Clienti distinti con deployment su questa versione
  const ids = [
    ...new Set(deployments.filter((d) => d.releaseVersion === version).map((d) => d.customerId)),
  ];
  const nomi = ids.map((id) => customerMap.get(id) ?? String(id));

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className={styles.linkCell}
        onClick={() => setAperto((v) => !v)}
        data-testid={`btn-clienti-${version}`}
        aria-expanded={aperto}
      >
        {nomi.length === 0 ? 'Nessuna Fornitura' : 'Clienti'}
      </button>
      {aperto && (
        <div
          className={styles.clientiPopup}
          data-testid={`clienti-release-${version}`}
          role="dialog"
          aria-label={`Clienti della release ${version}`}
        >
          {nomi.length === 0 ? (
            <span style={{ color: '#999' }}>Nessun cliente per questa release.</span>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {nomi.map((nome, i) => (
                <li key={ids[i]} style={{ padding: '0.1rem 0' }}>
                  {nome}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Release archiviate (vista su richiesta, con ripristino)
// ---------------------------------------------------------------------------

/** Elenco compatto delle release archiviate del prodotto, con "Ripristina" (se permesso). */
function ReleaseArchiviate({ productId, puoGestire }: { productId: string; puoGestire: boolean }) {
  const { data: archiviate, isLoading, isError, error } = useArchivedReleases(productId, true);
  const ripristina = useSetReleaseArchived(productId);
  return (
    <section
      className={styles.archiviatePanel}
      aria-label="Release archiviate"
      data-testid="sezione-release-archiviate"
    >
      <p className={styles.archiviateTitle}>Release archiviate</p>
      {isLoading && <p className={styles.loadingMsg}>Caricamento…</p>}
      {isError && (
        <p className={styles.errorMsg}>
          {error instanceof Error ? error.message : 'Errore nel caricamento delle archiviate'}
        </p>
      )}
      {archiviate && archiviate.length === 0 && (
        <p className={styles.bundleEmpty} data-testid="release-archiviate-vuote">
          Nessuna release archiviata.
        </p>
      )}
      {archiviate && archiviate.length > 0 && (
        <ul className={styles.archiviateList}>
          {archiviate.map((r) => (
            <li
              key={r.version}
              className={styles.archiviataRow}
              data-testid={`release-archiviata-${r.version}`}
            >
              <span className={styles.relVersion}>{r.version}</span>
              {r.archivedAt && (
                <span className={styles.archiviataData}>
                  archiviata il {new Date(r.archivedAt).toLocaleDateString('it-IT')}
                </span>
              )}
              {puoGestire && (
                <button
                  type="button"
                  className={`${styles.btnSecondary} ${styles.btnSmall}`}
                  onClick={() => ripristina.mutate({ version: r.version, archived: false })}
                  disabled={ripristina.isPending}
                  data-testid={`btn-ripristina-release-${r.version}`}
                >
                  Ripristina
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {ripristina.isError && (
        <span className={styles.inlineMutError} role="alert">
          {ripristina.error.message}
        </span>
      )}
    </section>
  );
}

/** Rispetta "riduci movimento" del sistema operativo (niente scroll animato). */
function preferisceMenoMovimento(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// ---------------------------------------------------------------------------
// Sezione Release principale
// ---------------------------------------------------------------------------

/** Sezione Release del dettaglio prodotto. */
export function ReleaseSection({ productId }: { productId: string }) {
  const { can } = usePermissions();
  const { data: releases, isLoading, isError, error } = useReleases(productId);
  // Prodotto (per il cpePrefix, usato per comporre il CPE completo di ogni release
  // insieme al suo cpeSuffix). Stessa queryKey già popolata da Anagrafica → cache hit.
  const { data: product } = useProduct(productId);

  const puoGestire = can('PORTFOLIO_MANAGEMENT');

  // Slice 6 — dati deployments e clienti per la colonna "Clienti"
  const { data: deployments = [], isSuccess: fornitureCaricate } = useDeployments(productId);
  const { data: clienti = [] } = useCustomers();
  const customerMap = new Map(clienti.map((c) => [c.id, c.name]));
  // Slice 8c — bubble-up vulnerabilità aperte: badge sulle release con ≥1 bundle aperto.
  const { data: openSets } = useOpenVulnSets();
  // Pending-scan — bubble-up: badge sulle release/bundle modificati dopo l'ultima scansione.
  const { data: pendingSets } = usePendingScanSets();

  // Il form "Nuova release" è chiuso di default quando esiste già almeno una release
  // (lista non vuota); resta aperto quando non ce ne sono ancora. `null` = nessuna
  // interazione utente → si usa il default derivato; un click fissa la scelta manuale.
  const haReleases = (releases?.length ?? 0) > 0;
  const [formAperto, setFormAperto] = useState<boolean | null>(null);
  const mostraForm = formAperto ?? !haReleases;
  // Vista "Archiviate" (lettura su richiesta).
  const [mostraArchiviate, setMostraArchiviate] = useState(false);

  // Dopo "Crea release": la card della nuova release riceve scroll + focus appena compare.
  const cardRefs = useRef(new Map<string, HTMLDivElement>());
  const [daMostrare, setDaMostrare] = useState<string | null>(null);
  function handleReleaseCreata(version: string) {
    setFormAperto(false);
    // La nuova release non ha vulnerabilità: col filtro attivo non sarebbe visibile (e il default
    // del filtro non deve più riattivarlo).
    setSoloConVuln(false);
    defaultInizializzato.current = true;
    setDaMostrare(version);
  }

  // Filtro: mostra solo le release con ≥1 bundle con vulnerabilità aperte (releaseUuids dal
  // bubble-up). Default derivato dai dati: ON se esiste almeno una release con vulnerabilità
  // attive, altrimenti OFF (mostra tutte). Si inizializza una sola volta al primo caricamento
  // completo di releases+openSets — dopo, il toggle manuale dell'utente ha sempre la priorità.
  const [soloConVuln, setSoloConVuln] = useState(false);
  // Modalità "Confronta release": pannello di scelta delle due release + diff.
  const [confronto, setConfronto] = useState(false);
  const defaultInizializzato = useRef(false);
  useEffect(() => {
    if (defaultInizializzato.current) return;
    if (releases === undefined || openSets === undefined) return;
    const esisteReleaseConVuln = releases.some(
      (r) => !!(r.dtProjectUuid && openSets.releaseUuids.has(r.dtProjectUuid)),
    );
    setSoloConVuln(esisteReleaseConVuln);
    defaultInizializzato.current = true;
  }, [releases, openSets]);
  const releasesVisibili =
    releases === undefined
      ? []
      : soloConVuln
        ? releases.filter((r) => !!(r.dtProjectUuid && openSets?.releaseUuids.has(r.dtProjectUuid)))
        : releases;

  useEffect(() => {
    if (daMostrare === null) return;
    const card = cardRefs.current.get(daMostrare);
    if (!card) return; // la lista non è ancora stata riletta: riprova al prossimo render
    const ridotto = preferisceMenoMovimento();
    card.scrollIntoView?.({ behavior: ridotto ? 'auto' : 'smooth', block: 'start' });
    card.focus({ preventScroll: true });
    setDaMostrare(null);
  }, [daMostrare, releasesVisibili]);

  return (
    <div data-testid="sezione-release">
      {puoGestire && (
        <div className={styles.nuovaReleaseWrap}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => setFormAperto(!mostraForm)}
            aria-expanded={mostraForm}
            data-testid="btn-toggle-nuova-release"
          >
            {mostraForm ? '− Nuova release' : '+ Nuova release'}
          </button>
          {mostraForm && <NuovaReleaseForm productId={productId} onCreated={handleReleaseCreata} />}
        </div>
      )}

      {isLoading && (
        <p className={styles.loadingMsg} data-testid="loading-release">
          Caricamento release…
        </p>
      )}

      {isError && (
        <p className={styles.errorMsg} data-testid="errore-release">
          {error instanceof Error ? error.message : 'Errore nel caricamento delle release'}
        </p>
      )}

      {!isLoading && !isError && releases !== undefined && releases.length === 0 && (
        <>
          <div className={styles.emptyState} data-testid="release-vuote">
            Nessuna release registrata.
          </div>
          <div className={styles.releaseFilterBar}>
            <BottoneArchiviate aperto={mostraArchiviate} onToggle={setMostraArchiviate} />
          </div>
          {mostraArchiviate && <ReleaseArchiviate productId={productId} puoGestire={puoGestire} />}
        </>
      )}

      {!isLoading && !isError && releases !== undefined && releases.length > 0 && (
        <>
          <div className={styles.releaseFilterBar}>
            <label className={styles.scopeCheck}>
              <input
                type="checkbox"
                checked={soloConVuln}
                onChange={(e) => setSoloConVuln(e.target.checked)}
                data-testid="toggle-solo-con-vuln"
              />
              Solo release con vulnerabilità
            </label>
            <button
              type="button"
              className={`${styles.btnSecondary} ${styles.btnSmall}`}
              onClick={() => setConfronto((v) => !v)}
              aria-pressed={confronto}
              disabled={releases.length < 2}
              title={
                releases.length < 2
                  ? 'Servono almeno due release'
                  : 'Confronta due release: metadati, bundle, BOM e componenti'
              }
              data-testid="btn-confronta-release"
            >
              Confronta release
            </button>
            <BottoneArchiviate aperto={mostraArchiviate} onToggle={setMostraArchiviate} />
          </div>

          {mostraArchiviate && <ReleaseArchiviate productId={productId} puoGestire={puoGestire} />}

          {confronto && (
            <ReleaseCompare
              productId={productId}
              versions={releases.map((r) => r.version)}
              onClose={() => setConfronto(false)}
            />
          )}

          {releasesVisibili.length === 0 ? (
            <div className={styles.emptyState} data-testid="release-filtrate-vuote">
              Nessuna release con vulnerabilità aperte.
            </div>
          ) : (
            <div className={styles.releaseList} data-testid="tabella-release">
              {releasesVisibili.map((r) => (
                <ReleaseCard
                  key={r.version}
                  productId={productId}
                  version={r.version}
                  dtProjectUuid={r.dtProjectUuid}
                  cpeFull={resolveReleaseCpe(product?.cpePrefix, r.cpeSuffix)}
                  purl={r.purl}
                  puoGestire={puoGestire}
                  deployments={deployments}
                  customerMap={customerMap}
                  openSets={openSets}
                  pendingSets={pendingSets}
                  fornitureCaricate={fornitureCaricate}
                  cardRef={(el) => {
                    if (el) cardRefs.current.set(r.version, el);
                    else cardRefs.current.delete(r.version);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Mostra/nasconde la vista delle release archiviate. */
function BottoneArchiviate({
  aperto,
  onToggle,
}: {
  aperto: boolean;
  onToggle: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      className={`${styles.btnSecondary} ${styles.btnSmall}`}
      onClick={() => onToggle(!aperto)}
      aria-expanded={aperto}
      title="Release archiviate (senza forniture): consultale o ripristinale"
      data-testid="btn-toggle-archiviate"
    >
      {aperto ? 'Nascondi archiviate' : 'Archiviate'}
    </button>
  );
}

// Workaround: ChangeEvent usato nei test-type-check
export type { ChangeEvent };
